// Vanilla Leaflet wrapper. Mounts on a ref'd div, draws the polyline,
// fits bounds, and tears down on unmount. No react-leaflet — keeps the
// React 19 / Leaflet 1.9 interaction trivial.

import { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import css from './RouteMap.module.css';

interface Point {
  lat: number;
  lon: number;
  elevation?: number;
  time?: number;
  heart_rate?: number;
}

interface Bounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

interface Props {
  points: Point[];
  bounds: Bounds | null;
}

export function RouteMap({ points, bounds }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current, {
      attributionControl: true,
      zoomControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    if (points.length > 1) {
      const latlngs: L.LatLngTuple[] = points.map((p) => [p.lat, p.lon]);
      L.polyline(latlngs, {
        color: '#4ade80',
        weight: 3,
        opacity: 0.9,
      }).addTo(map);
      // Start marker (circle)
      L.circleMarker([points[0]!.lat, points[0]!.lon], {
        radius: 6,
        color: '#fff',
        fillColor: '#4ade80',
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);
      // End marker
      const last = points[points.length - 1]!;
      L.circleMarker([last.lat, last.lon], {
        radius: 6,
        color: '#fff',
        fillColor: '#ef4444',
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);
    }

    if (bounds) {
      map.fitBounds(
        [
          [bounds.minLat, bounds.minLon],
          [bounds.maxLat, bounds.maxLon],
        ],
        { padding: [24, 24] },
      );
    } else {
      map.setView([0, 0], 2);
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [points, bounds]);

  if (points.length === 0) {
    return (
      <div className={css.emptyMap}>
        no GPS points in this activity (indoor / no fix)
      </div>
    );
  }
  return <div ref={containerRef} className={css.map} />;
}
